import { Injectable } from '@angular/core';

export interface TemplateAData {
    headerLogoUrl?: string;
    subheaderImage1Url?: string;
    subheaderImage2Url?: string;
    bodyHtml?: string;
    bottomRightImageUrl?: string;
}

@Injectable({
    providedIn: 'root'
})
export class EmailTemplateGeneratorService {

    constructor() { }

    /**
     * Parse template JSON string and return typed object
     */
    public parseTemplateJson(jsonString: string): TemplateAData {
        try {
            if (!jsonString) {
                return this.getDefaultTemplateAJson();
            }
            const parsed = JSON.parse(jsonString);
            return {
                headerLogoUrl: parsed.headerLogoUrl || '',
                subheaderImage1Url: parsed.subheaderImage1Url || '',
                subheaderImage2Url: parsed.subheaderImage2Url || '',
                bodyHtml: parsed.bodyHtml || '',
                bottomRightImageUrl: parsed.bottomRightImageUrl || ''
            };
        } catch (error) {
            console.error('Error parsing template JSON:', error);
            return this.getDefaultTemplateAJson();
        }
    }

    /**
     * Get default/empty template data structure
     */
    public getDefaultTemplateAJson(): TemplateAData {
        return {
            headerLogoUrl: '',
            subheaderImage1Url: '',
            subheaderImage2Url: '',
            bodyHtml: '',
            bottomRightImageUrl: ''
        };
    }

    /**
     * Generate complete HTML from TemplateA structure and user's JSON data
     * @param templateData The template customization data
     */
    public generateTemplateAHtml(templateData: TemplateAData): string {
        const data = { ...this.getDefaultTemplateAJson(), ...templateData };

        return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background-color: #ffffff;
        }
        .email-container {
            width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
        }
        .header-section {
            width: 100%;
            padding: 20px;
            background-color: #ffffff;
        }
        .header-logo {
            text-align: center;
            padding-bottom: 15px;
        }
        .header-logo img {
            max-width: 300px;
            height: auto;
            max-height: 100px;
        }
        .header-images {
            text-align: center;
        }
        .header-images img {
            max-width: 45%;
            height: auto;
            max-height: 150px;
            margin: 0 2%;
            display: inline-block;
        }
        .body-section {
            padding: 20px 20px;
            background-color: #ffffff;
            line-height: 1.4;
            text-align: left;
        }
        .body-section p {
            text-align: left !important;
            margin: 4px 0;
        }
        .body-section div,
        .body-section span {
            text-align: left !important;
        }
        @media only screen and (max-width: 600px) {
            .header-images img {
                max-width: 45%;
            }
        }
    </style>
</head>
<body>
    <table width="100%" border="0" cellpadding="0" cellspacing="0">
        <tr>
            <td align="center">
                <div class="email-container">
                    <!-- Header Section -->
                    <div class="header-section">
                        <div class="header-logo">
                            ${data.headerLogoUrl ? `<img src="${data.headerLogoUrl}" alt="Company Logo">` : ''}
                        </div>
                        <div class="header-images">
                            ${data.subheaderImage1Url ? `<img src="${data.subheaderImage1Url}" alt="Header Image 1">` : ''}
                            ${data.subheaderImage2Url ? `<img src="${data.subheaderImage2Url}" alt="Header Image 2">` : ''}
                        </div>
                    </div>

                    <!-- Body Section -->
                    <div class="body-section">
                        ${data.bodyHtml || '<p>Email content goes here...</p>'}
                    </div>

                    <!-- Pricing and Bottom Image Section -->
                    <table style="width:100%; border-collapse:collapse;">
                        <tr>
                            <td style="width:60%; vertical-align:top; padding:20px; text-align:center;">
                                {{pricingTable}}
                            </td>
                            <td style="width:40%; vertical-align:middle; text-align:center; padding:20px;">
                                ${data.bottomRightImageUrl ? `<img src="${data.bottomRightImageUrl}" alt="Bottom Image" style="max-width:100%; height:auto; max-height:200px;">` : ''}
                            </td>
                        </tr>
                    </table>
                </div>
            </td>
        </tr>
    </table>
</body>
</html>
        `.trim();
    }

    /**
     * Generate HTML pricing table with volume tiers matching the email format
     */
    public generatePricingTableHtml(pricingData: any): string {
        if (!pricingData?.products || pricingData.products.length === 0) {
            return '<p style="color:#888;">No current pricing available.</p>';
        }

        let tablesHtml = '';
        pricingData.products.forEach((product: any) => {
            if (!product.tiers || product.tiers.length === 0) { return; }

            const rows = product.tiers
                .map((tier: any) => {
                    const volumeRange = tier.maxGallons > 0
                        ? `${Math.floor(tier.minGallons)}&nbsp;-&nbsp;${Math.floor(tier.maxGallons)}`
                        : `${Math.floor(tier.minGallons)}+`;
                    return `<tr>
                        <td style="font-family: Arial; font-size: 13px; border: 1px solid black; padding: 4px;">${volumeRange}</td>
                        <td style="font-family: Arial; font-size: 13px; border: 1px solid black; padding: 4px;">$${Number(tier.price).toFixed(2)}</td>
                    </tr>`;
                })
                .join('');

            tablesHtml += `
                <table cellspacing="0" cellpadding="0" style="border-collapse:collapse; margin:0 auto 10px auto;">
                    <thead>
                        <tr><th colspan="2" style="font-family: Arial; font-size: 13px; padding: 4px; text-align: center;">${product.product} Pricing</th></tr>
                        <tr>
                            <th style="font-family: Arial; font-size: 13px; border: 1px solid black; padding: 4px; font-weight: bold;">Volume Tier</th>
                            <th style="font-family: Arial; font-size: 13px; border: 1px solid black; padding: 4px; font-weight: bold;">All Flights</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>`;
        });

        return tablesHtml;
    }

    /**
     * Generate placeholder message when email template is not linked to a pricing template
     */
    public generatePricingNotLinkedHtml(): string {
        return '<p style="text-align:center; color:#888; font-style:italic; padding:20px;">Please link email template to ITP Margin Template to see pricing in email example.</p>';
    }
}
